import React, { useState, useEffect } from 'react';

import Select from 'react-select';

import { validateFormField } from 'Helpers/formValidation';
import { listOfResources, multiSelectConfigs, onChangeUserAssociations, prepareRelationsData } from 'Helpers/multi-select';

import FormInput from 'CommonComponents/FormInput';
import ModalFooter from 'CommonComponents/ModalFooter';
import ModalHeader from 'CommonComponents/ModalHeader';
import SelectInput from 'CommonComponents/SelectInput';

function UsersCreate(props) {
    const isOrganisationUser = () => {
        return props.type && props.type === 'Organisation';
    };

    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [email, setEmail] = useState('');
    const [userId, setUserId] = useState('');
    const [rolesData, setRolesData] = useState([]);
    const [departmentsData, setDepartmentsData] = useState([]);
    const [revDepartmentsData, setRevDepartmentsData] = useState([]);
    const [costcentersData, setCostcentersData] = useState([]);
    const [revCostcentersData, setRevCostcentersData] = useState([]);
    const [venuesData, setVenuesData] = useState([]);
    const [modulesData, setModulesData] = useState([]);

    const defaultErrors = isOrganisationUser()
        ? {
              firstName: null,
              lastName: null,
              email: null,
          }
        : { userId: null };

    const [formErrors, setFormErrors] = useState(defaultErrors);
    const [formDisabled, setFormDisabled] = useState(true);
    const [requestErrors, setRequestErrors] = useState('');

    const styles = { ...multiSelectConfigs };
    const listOfRoles = listOfResources(props.roles);
    const listOfDepartments = listOfResources(props.departments);
    const listOfRevDepartments = listOfResources(props.revDepartments);
    const listOfVenues = listOfResources(props.venues);
    const listOfCostcenters = listOfResources(props.costcenters);
    const listOfRevCostcenters = listOfResources(props.revCostcenters);
    const listOfModules = listOfResources(props.modules || []);

    const formHasErrors = () => {
        let hasErrors = false;

        Object.values(formErrors).forEach((val) => {
            if (val !== null) {
                hasErrors = true;
            }
        });

        return hasErrors;
    };

    const formFilled = () => {
        if (isOrganisationUser()) {
            return firstName.length > 1 && lastName.length > 1 && email.length;
        }

        return !!userId;
    };

    useEffect(() => {
        setFormDisabled(formHasErrors() || !formFilled());
    }, [formErrors]);

    useEffect(() => {
        setFormDisabled(true);
    }, []);

    const clearForm = () => {
        setFirstName('');
        setLastName('');
        setEmail('');
        setUserId('');
        setRolesData([]);
        setDepartmentsData([]);
        setRevDepartmentsData([]);
        setCostcentersData([]);
        setRevCostcentersData([]);
        setVenuesData([]);
        setModulesData([]);
        setFormErrors(defaultErrors);
        setRequestErrors('');
        setFormDisabled(true);
    };

    const handleClose = () => {
        clearForm();
        props.modalElement && props.modalElement.hide();
    };

    const handleSave = () => {
        validateForm();
        if (formHasErrors()) {
            return;
        }
        if (!props.canCreate) {
            // alert('You do not have permission to create a new user.');
            setRequestErrors($t('You do not have permission to create a new user.'));
            props.modalElement && props.modalElement.hide();
            clearForm();

            return;
        }

        setFormDisabled(true);
        const { rolesIds, departmentsIds, venuesIds, costcentersIds, revdepartmentsIds, revcostcentersIds, modulesIds } = prepareRelationsData(
            rolesData,
            departmentsData,
            venuesData,
            costcentersData,
            revDepartmentsData,
            revCostcentersData,
            modulesData,
        );

        let data = isOrganisationUser()
            ? {
                  firstName,
                  lastName,
                  email,
                  rolesIds,
              }
            : { userId, rolesIds };

        if (!isOrganisationUser()) {
            data = {
                ...data,
                ...{
                    departmentsIds,
                    costcentersIds,
                    revdepartmentsIds,
                    revcostcentersIds,
                    venuesIds,
                    modulesIds,
                },
            };
        }

        axios
            .post(props.storeUserUrl, data)
            .then((response) => {
                if (response?.data?.exceeded) {
                    alert($t(`You have exceeded the designated number of users. Please contact the InBudget admin.`));
                }
                props.fetchUsersHandler();
                setFormDisabled(false);
                clearForm();
                props.modalElement && props.modalElement.hide();
            })
            .catch((e) => {
                console.log(e.response);
                if (e.response?.data?.existing_organization_user === true && e.response?.data?.already_confirmed_organization_user === true) {
                    setRequestErrors($t(`The user is already in the organization, to edit the information click the edit button in the list.`));
                } else if (e.response?.data?.existing_organization_user === true) {
                    setRequestErrors($t(`The user is already invited to the organization, to send a new invitation click Resend invite in the list.`));
                } else {
                    setRequestErrors(e.response.data.error || e.response.data.message || $t('Somethings went wrong!'));
                }
                setFormDisabled(false);
            });
    };

    const firstNameChange = (e) => {
        setFirstName(e.target.value);
    };

    const lastNameChange = (e) => {
        setLastName(e.target.value);
    };

    const emailChange = (e) => {
        setEmail(e.target.value);
    };

    const userIdChange = (e) => {
        setUserId(e.target.value);
        validateForm();
    };

    function onChangeRoles(e, option) {
        onChangeUserAssociations(e, option, setRolesData);
    }

    function onChangeDepartments(e, option) {
        onChangeUserAssociations(e, option, setDepartmentsData);
    }

    function onChangeRevDepartments(e, option) {
        onChangeUserAssociations(e, option, setRevDepartmentsData);
    }

    function onChangeVenues(e, option) {
        onChangeUserAssociations(e, option, setVenuesData);
    }

    const onChangeModules = (e, option) => {
        onChangeUserAssociations(e, option, setModulesData);
    };

    function onChangeCostcenters(e, option) {
        onChangeUserAssociations(e, option, setCostcentersData);
    }

    function onChangeRevCostcenters(e, option) {
        onChangeUserAssociations(e, option, setRevCostcentersData);
    }

    const validateField = (fieldName, value) => {
        const errors = { ...formErrors };
        const validationErrors = validateFormField(fieldName, value);
        Object.keys(validationErrors).forEach((key) => {
            if (key in errors) {
                errors[key] = validationErrors[key];
            }
        });

        setFormErrors(errors);
    };

    const validateForm = () => {
        if (isOrganisationUser()) {
            validateField('email', email);
            validateField('firstName', firstName);
            validateField('lastName', lastName);
        } else {
            validateField('userId', userId);
        }
    };

    return (
        <div id={props.modalId} ref={props.modalRef} tabIndex="-1" aria-hidden="true" className="hidden modal__box">
            <div className="modal__container">
                <div className="modal__inner-container">
                    <ModalHeader title={$t('Add New User')} close={handleClose}></ModalHeader>
                    <div className="tw-rounded-b">
                        <div className="modal__form-container">
                            <div className={!requestErrors ? 'modal__form-container' : 'hidden'}>
                                {isOrganisationUser() && (
                                    <>
                                        <FormInput
                                            horizontalLayout={true}
                                            label={$t('First Name')}
                                            required={true}
                                            type="text"
                                            id="inputFirstName"
                                            placeholder={$t('First Name')}
                                            value={firstName}
                                            onChange={firstNameChange}
                                            onBlur={() => {
                                                validateField('firstName', firstName);
                                            }}
                                            showError={formErrors.firstName}
                                            errorText={formErrors.firstName}
                                        ></FormInput>

                                        <FormInput
                                            horizontalLayout={true}
                                            label={$t('Last Name')}
                                            required={true}
                                            type="text"
                                            id="inputLastName"
                                            placeholder={$t('Last Name')}
                                            value={lastName}
                                            onChange={lastNameChange}
                                            onBlur={() => {
                                                validateField('lastName', lastName);
                                            }}
                                            showError={formErrors.lastName}
                                            errorText={formErrors.lastName}
                                        ></FormInput>

                                        <FormInput
                                            horizontalLayout={true}
                                            label={$t('Email')}
                                            required={true}
                                            type="email"
                                            id="inputEmail"
                                            placeholder={$t('Email')}
                                            value={email}
                                            onChange={emailChange}
                                            onBlur={() => {
                                                validateField('email', email);
                                            }}
                                            showError={formErrors.email}
                                            errorText={formErrors.email}
                                        ></FormInput>
                                    </>
                                )}

                                {!isOrganisationUser() && (
                                    <>
                                        <SelectInput horizontalLayout={true} label={$t('Users')} required={true} value={userId} onChange={userIdChange}>
                                            {!userId && <option value={''}>Select User</option>}
                                            {props.organisationUsers.map((item) => {
                                                return (
                                                    <option key={item.id} value={item.id}>
                                                        {item.first_name} {item.last_name}
                                                    </option>
                                                );
                                            })}
                                        </SelectInput>
                                    </>
                                )}

                                <div className="tw-grid tw-grid-cols-3 tw-gap-4">
                                    <div className="tw-col-span-1">
                                        <label className="tw-text-charcoal tw-text-sm tw-mb-0">{$t('Roles')}</label>
                                    </div>
                                    <div className="tw-flex tw-col-span-2 tw-flex-col tw-space-y-1.5">
                                        <Select
                                            value={rolesData}
                                            isMulti
                                            name="roles"
                                            options={listOfRoles || []}
                                            className="react-select-container"
                                            classNamePrefix="react-select"
                                            onChange={onChangeRoles}
                                            styles={styles}
                                        />
                                    </div>
                                </div>

                                {!isOrganisationUser() && (
                                    <>
                                        <div className="tw-grid tw-grid-cols-3 tw-gap-4">
                                            <div className="tw-col-span-1">
                                                <label className="tw-text-charcoal tw-text-sm tw-mb-0">{$t('Modules')}</label>
                                            </div>
                                            <div className="tw-flex tw-col-span-2 tw-flex-col tw-space-y-1.5">
                                                <Select
                                                    value={modulesData}
                                                    isMulti
                                                    name="modules"
                                                    options={listOfModules || []}
                                                    className="react-select-container"
                                                    classNamePrefix="react-select"
                                                    onChange={onChangeModules}
                                                    styles={styles}
                                                />
                                            </div>
                                        </div>
                                        <div className="tw-grid tw-grid-cols-3 tw-gap-4">
                                            <div className="tw-col-span-1">
                                                <label className="tw-text-charcoal tw-text-sm tw-mb-0">{$t('Venues')}</label>
                                            </div>
                                            <div className="tw-flex tw-col-span-2 tw-flex-col tw-space-y-1.5">
                                                <Select
                                                    value={venuesData}
                                                    isMulti
                                                    name="venues"
                                                    options={listOfVenues || []}
                                                    className="react-select-container"
                                                    classNamePrefix="react-select"
                                                    onChange={onChangeVenues}
                                                    styles={styles}
                                                />
                                            </div>
                                        </div>
                                        <div className="tw-grid tw-grid-cols-3 tw-gap-4">
                                            <div className="tw-col-span-1">
                                                <label className="tw-text-charcoal tw-text-sm tw-mb-0">{$t('Departments')}</label>
                                            </div>
                                            <div className="tw-flex tw-col-span-2 tw-flex-col tw-space-y-1.5">
                                                <Select
                                                    value={departmentsData}
                                                    isMulti
                                                    name="departments"
                                                    options={listOfDepartments || []}
                                                    className="react-select-container"
                                                    classNamePrefix="react-select"
                                                    onChange={onChangeDepartments}
                                                    styles={styles}
                                                />
                                            </div>
                                        </div>
                                        <div className="tw-grid tw-grid-cols-3 tw-gap-4">
                                            <div className="tw-col-span-1">
                                                <label className="tw-text-charcoal tw-text-sm tw-mb-0">{$t('Cost Centers')}</label>
                                            </div>
                                            <div className="tw-flex tw-col-span-2 tw-flex-col tw-space-y-1.5">
                                                <Select
                                                    value={costcentersData}
                                                    isMulti
                                                    name="costcenters"
                                                    options={listOfCostcenters || []}
                                                    className="react-select-container"
                                                    classNamePrefix="react-select"
                                                    onChange={onChangeCostcenters}
                                                    styles={styles}
                                                />
                                            </div>
                                        </div>
                                        <div className="tw-grid tw-grid-cols-3 tw-gap-4">
                                            <div className="tw-col-span-1">
                                                <label className="tw-text-charcoal tw-text-sm tw-mb-0">{$t('Rev Departments')}</label>
                                            </div>
                                            <div className="tw-flex tw-col-span-2 tw-flex-col tw-space-y-1.5">
                                                <Select
                                                    value={revDepartmentsData}
                                                    isMulti
                                                    name="revDepartments"
                                                    options={listOfRevDepartments || []}
                                                    className="react-select-container"
                                                    classNamePrefix="react-select"
                                                    onChange={onChangeRevDepartments}
                                                    styles={styles}
                                                />
                                            </div>
                                        </div>
                                        <div className="tw-grid tw-grid-cols-3 tw-gap-4">
                                            <div className="tw-col-span-1">
                                                <label className="tw-text-charcoal tw-text-sm tw-mb-0">{$t('Rev Cost Centers')}</label>
                                            </div>
                                            <div className="tw-flex tw-col-span-2 tw-flex-col tw-space-y-1.5">
                                                <Select
                                                    value={revCostcentersData}
                                                    isMulti
                                                    name="revCoctcenters"
                                                    options={listOfRevCostcenters || []}
                                                    className="react-select-container"
                                                    classNamePrefix="react-select"
                                                    onChange={onChangeRevCostcenters}
                                                    styles={styles}
                                                />
                                            </div>
                                        </div>
                                    </>
                                )}

                                <ModalFooter
                                    cancelButtonText={$t('Close')}
                                    primaryButtonText={$t('Save')}
                                    onCancel={handleClose}
                                    handleSubmit={handleSave}
                                    allowSubmit={!requestErrors}
                                    isSubmitting={formDisabled}
                                ></ModalFooter>
                            </div>
                            <div className={requestErrors ? 'tw-grid tw-grid-cols-1' : 'hidden'}>
                                <label className="modal__msg">{requestErrors}</label>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default UsersCreate;
